import { buildImageProxyUrl } from './image-proxy';
import type {
  OfferCampaign,
  OfferCampaignStatus,
  PublicLiveOfferCampaignSummary,
  PublicOfferCampaignPage,
  PublicOfferCampaignProduct,
} from '../types/offer-campaign';
import type { PublicProductCard } from '../types/product';

const PUBLIC_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

interface PublicOfferCampaignProductRow extends PublicProductCard {
  readonly campaign_variant_id?: unknown;
  readonly campaign_variant_option_summary?: unknown;
  readonly campaign_variant_is_default?: unknown;
  readonly campaign_regular_price_cents?: unknown;
  readonly campaign_sale_price_cents?: unknown;
  readonly campaign_effective_price_cents?: unknown;
}

export function deriveOfferCampaignStatus(
  input: Pick<OfferCampaign, 'is_active' | 'starts_at' | 'ends_at'>,
  now: Date = new Date(),
): OfferCampaignStatus {
  if (!input.is_active) return 'inactive';
  const startsAt = Date.parse(input.starts_at);
  const endsAt = Date.parse(input.ends_at);
  const current = now.getTime();
  if (!Number.isFinite(startsAt) || !Number.isFinite(endsAt)) return 'inactive';
  if (current < startsAt) return 'scheduled';
  if (current >= endsAt) return 'ended';
  return 'live';
}

export function isLiveOfferCampaign(
  input: Pick<OfferCampaign, 'is_active' | 'starts_at' | 'ends_at'>,
  now: Date = new Date(),
): boolean {
  return deriveOfferCampaignStatus(input, now) === 'live';
}

export function normalizeOfferCampaignPublicSlug(value: string): string | null {
  const normalized = value.trim().toLowerCase();
  return PUBLIC_SLUG_PATTERN.test(normalized) ? normalized : null;
}

export function buildOfferCampaignTimerParts(
  endsAt: string,
  now: Date = new Date(),
): {
  readonly totalSeconds: number;
  readonly days: number;
  readonly hours: number;
  readonly minutes: number;
  readonly seconds: number;
} {
  const totalSeconds = Math.max(0, Math.floor((Date.parse(endsAt) - now.getTime()) / 1000));
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  return { totalSeconds, days, hours, minutes, seconds };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readString(source: Record<string, unknown>, key: string): string | null {
  const value = source[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function readNullableString(source: Record<string, unknown>, key: string): string | null {
  const value = source[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function hydrateCampaignProducts(
  rows: readonly PublicOfferCampaignProductRow[],
  supabaseUrl: string,
): PublicOfferCampaignProduct[] {
  return rows.flatMap((row) => {
    const campaignVariantId =
      typeof row.campaign_variant_id === 'string' ? row.campaign_variant_id : null;
    const optionSummary =
      typeof row.campaign_variant_option_summary === 'string'
        ? row.campaign_variant_option_summary
        : '';
    const regularPrice =
      typeof row.campaign_regular_price_cents === 'number'
        ? row.campaign_regular_price_cents
        : null;
    const salePrice =
      typeof row.campaign_sale_price_cents === 'number' ? row.campaign_sale_price_cents : null;
    const effectivePrice =
      typeof row.campaign_effective_price_cents === 'number'
        ? row.campaign_effective_price_cents
        : null;
    const isDefault =
      typeof row.campaign_variant_is_default === 'boolean'
        ? row.campaign_variant_is_default
        : false;

    if (
      !campaignVariantId ||
      regularPrice === null ||
      salePrice === null ||
      effectivePrice === null
    ) {
      return [];
    }

    const { category_media_id: _categoryMediaId, ...product } = row;
    return [
      {
        ...product,
        image_url: row.primary_image_path
          ? buildImageProxyUrl(supabaseUrl, 'product-images', row.primary_image_path)
          : null,
        category: {
          id: row.category_id,
          name: row.category_name,
          image_url: row.category_media_id
            ? buildImageProxyUrl(supabaseUrl, 'category-media', row.category_media_id)
            : null,
        },
        campaign_variant_id: campaignVariantId,
        campaign_variant_option_summary: optionSummary,
        campaign_variant_is_default: isDefault,
        campaign_regular_price_cents: regularPrice,
        campaign_sale_price_cents: salePrice,
        campaign_effective_price_cents: effectivePrice,
      },
    ];
  });
}

function parseLiveSummary(
  value: unknown,
  supabaseUrl: string,
): PublicLiveOfferCampaignSummary | null {
  if (!isRecord(value)) return null;
  const id = readString(value, 'id');
  const storeSlug = readString(value, 'store_slug');
  const publicSlug = readString(value, 'public_slug');
  const title = readString(value, 'title');
  const startsAt = readString(value, 'starts_at');
  const endsAt = readString(value, 'ends_at');
  if (!id || !storeSlug || !publicSlug || !title || !startsAt || !endsAt) return null;

  const productRows = Array.isArray(value.products)
    ? (value.products as PublicOfferCampaignProductRow[])
    : [];

  return {
    id,
    store_slug: storeSlug,
    public_slug: publicSlug,
    title,
    short_description: readNullableString(value, 'short_description'),
    starts_at: startsAt,
    ends_at: endsAt,
    products: hydrateCampaignProducts(productRows, supabaseUrl),
  };
}

export function parsePublicOfferCampaignPagePayload(
  value: unknown,
  supabaseUrl: string,
): PublicOfferCampaignPage {
  if (!isRecord(value)) return { state: 'unavailable' };
  const state = readString(value, 'state');
  if (state === 'ended') return { state: 'ended' };
  if (state !== 'live') return { state: 'unavailable' };
  const live = parseLiveSummary(value, supabaseUrl);
  return live ? { state: 'live', ...live } : { state: 'unavailable' };
}

export function parsePublicLiveOfferCampaignPayload(
  value: unknown,
  supabaseUrl: string,
): PublicLiveOfferCampaignSummary | null {
  return parseLiveSummary(value, supabaseUrl);
}
